/**
 * usePositions.ts
 *
 * This file contains the hook and supporting functions for fetching and managing user positions.
 * It provides pagination, caching, and price data integration for position assets.
 *
 * The data flow:
 * 1. Fetch position IDs for an account with pagination (V2 first, newest first within each manager)
 * 2. Fetch detailed information for each position ID
 * 3. Extract token symbols from positions
 * 4. Fetch USD prices for all tokens
 * 5. Combine position details with pricing information
 *
 * Position ordering:
 * - V2 positions appear before V1 positions
 * - Within each position manager, newest positions (highest index) come first
 * - This ordering is applied at the pagination level for efficiency
 *
 * Caching strategy:
 * - Position data is cached for 15 seconds (DEFAULT_POSITIONS_STALE_TIME)
 * - Asset prices are cached for 60 seconds (DEFAULT_WAGMI_QUERY_ASSET_PRICE_STALE_TIME)
 * - Adjacent pages are prefetched for smoother pagination
 * - Individual position details are cached to minimize redundant network requests
 */

import { useAccount, useChainId } from 'wagmi';
import {
  keepPreviousData,
  useSuspenseQuery,
  useQueryClient,
  QueryClient,
} from '@tanstack/react-query';
import { readContract, readContracts } from '@wagmi/core';
import { useMemo, useEffect } from 'react';
import { verifiedPoolsPositionManagerAbi } from '@generated/generated';
import { wagmiConfig } from '@/onchain/OnchainProviders';
import { getAllPositionManagerAddresses } from '@/onchain/utils/hookConfig';
import {
  AssetUSDPriceMap,
  getAssetUSDPrice,
  QUERY_KEY_ASSET_USD_PRICE,
} from '@/onchain/hooks/useAssetUSDPrice';
import { ZERO_ADDR } from '@/utils/constants';
import {
  fetchPositionDetails,
  updatePositionDetailsPricing,
  QUERY_KEY_POSITION_DETAILS as POSITION_DETAILS_QUERY_KEY,
} from '@/onchain/hooks/usePositionDetails';
import {
  DEFAULT_POSITIONS_STALE_TIME,
  DEFAULT_WAGMI_QUERY_ASSET_PRICE_STALE_TIME,
} from '@/onchain/querySettings';

// =============================================
// Types
// =============================================

/**
 * Raw position details from the blockchain
 */
export type FetchedPositionDetails = Awaited<ReturnType<typeof fetchPositionDetails>>;

/**
 * Position details with USD pricing information
 */
export type PositionDetails = ReturnType<typeof updatePositionDetailsPricing>;

/**
 * Position ID with its associated position manager
 */
type PositionWithManager = {
  /** The position token ID */
  tokenId: bigint;
  /** The position manager address that owns this position */
  positionManagerAddress: `0x${string}`;
};

/**
 * Response structure when fetching position IDs with pagination
 */
type PositionIdsResponse = {
  /** Array of position token IDs with their position manager addresses */
  positions: PositionWithManager[];
  /** Pagination metadata */
  pagination: {
    /** Total number of positions owned by the user */
    totalItems: number;
    /** Total number of pages based on pageSize */
    totalPages: number;
    /** Current page index (0-based) */
    currentPage: number;
    /** Whether there are more positions on subsequent pages */
    hasMore: boolean;
  };
};

// =============================================
// Account Position IDs Functions
// =============================================

/**
 * Fetches position token IDs owned by an account with pagination
 *
 * This function performs two operations:
 * 1. Fetches the total number of positions owned by the account
 * 2. Fetches the specific position IDs for the requested page
 *
 * @param chainId - The chain ID to query
 * @param address - The owner's address
 * @param page - Zero-based page index
 * @param pageSize - Number of items per page
 * @returns Position token IDs and pagination metadata
 */
async function fetchAccountPositionIds({
  chainId,
  address,
  page,
  pageSize,
}: {
  chainId: number;
  address: `0x${string}`;
  page: number;
  pageSize: number;
}): Promise<PositionIdsResponse> {
  // Get all available position manager addresses (V2 first, then V1)
  const positionManagerAddresses = getAllPositionManagerAddresses(chainId);

  // Fetch balances from all position managers in parallel
  const balanceResults = await Promise.all(
    positionManagerAddresses.map(async (pmAddress) => {
      try {
        const balance = await readContract(wagmiConfig, {
          address: pmAddress,
          abi: verifiedPoolsPositionManagerAbi,
          functionName: 'balanceOf' as const,
          args: [address],
        });
        return { address: pmAddress, balance: Number(balance) };
      } catch (error) {
        // If this position manager doesn't exist on this chain, return 0 balance
        return { address: pmAddress, balance: 0 };
      }
    }),
  );

  // Calculate total items across all position managers
  const totalItems = balanceResults.reduce((sum, { balance }) => sum + balance, 0);
  const start = page * pageSize;
  const end = Math.min(start + pageSize, totalItems);

  // If requesting a page beyond available data, return empty array with pagination metadata
  if (start >= totalItems) {
    return {
      positions: [],
      pagination: {
        totalItems,
        totalPages: Math.ceil(totalItems / pageSize),
        currentPage: page,
        hasMore: false,
      },
    };
  }

  // Build a flat list of all position indices with their position manager address
  // V2 positions come first (since positionManagerAddresses is ordered V2, then V1)
  // Within each manager, reverse order (newest/highest index first)
  const allPositionIndices: { pmAddress: `0x${string}`; index: number }[] = [];
  for (const { address: pmAddress, balance } of balanceResults) {
    // Iterate from highest index to lowest (newest to oldest)
    for (let i = balance - 1; i >= 0; i--) {
      allPositionIndices.push({ pmAddress, index: i });
    }
  }

  // Get the slice for the current page
  const pageIndices = allPositionIndices.slice(start, end);

  // Fetch position token IDs for the current page
  const positionTokenIdsResult = await readContracts(wagmiConfig, {
    contracts: pageIndices.map(({ pmAddress, index }) => ({
      address: pmAddress,
      abi: verifiedPoolsPositionManagerAbi,
      functionName: 'tokenOfOwnerByIndex' as const,
      args: [address, index],
    })),
  });

  // Check if the position token ids were fetched successfully
  if (positionTokenIdsResult.some(({ error }) => error)) {
    throw new Error('Failed to fetch pool manager position tokens');
  }

  // Build the positions with their position manager addresses
  const positions: PositionWithManager[] = positionTokenIdsResult.map(({ result }, idx) => ({
    tokenId: result as bigint,
    positionManagerAddress: pageIndices[idx].pmAddress,
  }));

  return {
    positions,
    pagination: {
      totalItems,
      totalPages: Math.ceil(totalItems / pageSize),
      currentPage: page,
      hasMore: end < totalItems,
    },
  };
}

// =============================================
// Query Key and Function Definitions
// =============================================

/**
 * Query key for the combined positions with details query
 */
const QUERY_KEY_POSITIONS_WITH_DETAILS = (
  chainId: number,
  address: `0x${string}` | undefined,
  page: number,
  pageSize: number,
) => ['usePositionsWithDetails', chainId, address, page, pageSize];

// =============================================
// Combined Functions
// =============================================

/**
 * Fetches asset prices for a list of token symbols
 *
 * Uses TanStack Query caching to reduce redundant API calls to Coinbase
 *
 * @param queryClient - TanStack Query client for caching
 * @param assetSymbols - Array of token symbols to fetch prices for
 * @returns Map of token symbols to USD prices
 */
async function getAssetPricesMap<T extends string[]>(
  queryClient: QueryClient,
  assetSymbols: T,
): Promise<AssetUSDPriceMap<T>> {
  if (!assetSymbols.length) {
    return {} as AssetUSDPriceMap<T>;
  }

  // Fetch prices for all tokens with caching
  const pricesPromises = assetSymbols.map(async (symbol) => {
    try {
      // Use the imported query key function
      const queryKey = QUERY_KEY_ASSET_USD_PRICE(symbol);

      // Use queryClient.fetchQuery to leverage the cache
      return await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => getAssetUSDPrice(symbol),
        // Use the asset price stale time for consistency with useAssetUSDPrice
        staleTime: DEFAULT_WAGMI_QUERY_ASSET_PRICE_STALE_TIME,
      });
    } catch (error) {
      console.error(`Error fetching price for ${symbol}:`, error);
      return 0;
    }
  });

  const prices = await Promise.all(pricesPromises);

  // Convert to the expected format
  const data = assetSymbols.reduce((acc, symbol, index) => {
    acc[symbol as T[number]] = prices[index];
    return acc;
  }, {} as AssetUSDPriceMap<T>);

  return data;
}

/**
 * Fetches position details using the TanStack Query cache
 *
 * This allows sharing position detail data between different parts of the app
 *
 * @param queryClient - TanStack Query client for caching
 * @param chainId - The chain ID to query
 * @param address - The owner's address
 * @param positionId - The position ID to fetch details for
 * @returns Position details or null if there was an error
 */
async function fetchPositionDetailsWithCache(
  queryClient: QueryClient,
  chainId: number,
  address: `0x${string}`,
  positionId: bigint,
  positionManagerAddress?: `0x${string}`,
): Promise<FetchedPositionDetails | null> {
  try {
    // Use the same query key pattern as in usePositionDetails
    // Include positionManagerAddress in the query key to differentiate cached queries
    const queryKey = POSITION_DETAILS_QUERY_KEY(
      chainId,
      address,
      positionId,
      positionManagerAddress,
    );

    // Use fetchQuery to leverage the cache
    // Pass position manager address for efficient direct query
    return await queryClient.fetchQuery({
      queryKey,
      queryFn: async () => fetchPositionDetails(chainId, positionId, positionManagerAddress),
      // Match the main query's stale time
      staleTime: DEFAULT_POSITIONS_STALE_TIME,
    });
  } catch (error) {
    console.error(`Error fetching details for position ${positionId}:`, error);
    return null;
  }
}

/**
 * Combined function to fetch position IDs and their details with pricing
 *
 * Data flow:
 * 1. Fetch position IDs with pagination (already sorted: V2 first, newest first)
 * 2. Fetch details for each position ID in parallel
 * 3. Extract token symbols from all positions
 * 4. Fetch USD prices for all tokens
 * 5. Apply pricing to each position
 *
 * @param queryClient - TanStack Query client for caching
 * @param chainId - The chain ID to query
 * @param address - The owner's address
 * @param page - Zero-based page index
 * @param pageSize - Number of items per page
 * @returns Positions with details and pricing, plus pagination metadata
 */
async function fetchPositionsWithDetails({
  queryClient,
  chainId,
  address,
  page,
  pageSize,
}: {
  queryClient: QueryClient;
  chainId: number;
  address: `0x${string}`;
  page: number;
  pageSize: number;
}) {
  // Step 1: Fetch position IDs with pagination
  const positionIdsResponse = await fetchAccountPositionIds({
    chainId,
    address,
    page,
    pageSize,
  });

  // Early return if no positions
  if (positionIdsResponse.positions.length === 0) {
    return {
      positions: [],
      pagination: positionIdsResponse.pagination,
    };
  }

  // Step 2: Fetch details for each position ID in parallel using the query cache
  const positionDetailsPromises = positionIdsResponse.positions.map(
    async ({ tokenId, positionManagerAddress }) =>
      fetchPositionDetailsWithCache(queryClient, chainId, address, tokenId, positionManagerAddress),
  );

  const positionDetailsResults = await Promise.all(positionDetailsPromises);

  // Step 3: Get token symbols for pricing
  const tokenSymbols: string[] = [];
  positionDetailsResults.forEach((details) => {
    if (details?.positionTokens) {
      details.positionTokens.forEach((token) => {
        if (token.symbol && !tokenSymbols.includes(token.symbol)) {
          tokenSymbols.push(token.symbol);
        }
      });
    }
  });

  // Step 4: Fetch prices for all tokens using cached data
  const pricesMap = await getAssetPricesMap(queryClient, tokenSymbols);

  // Step 5: Apply pricing to positions
  // Note: Positions are already sorted (V2 first, newest first within each manager)
  // from the pagination logic in fetchAccountPositionIds
  const positionsWithPricing = positionDetailsResults
    .filter((details): details is FetchedPositionDetails => details !== null)
    .map((details) => updatePositionDetailsPricing(details, pricesMap));

  return {
    positions: positionsWithPricing,
    pagination: positionIdsResponse.pagination,
  };
}

/**
 * Combined hook that fetches user positions with all details
 *
 * Features:
 * - Pagination support
 * - Query caching for position details and token prices
 * - Prefetching of adjacent pages
 * - Automatic refetching when returning to tab
 *
 * When called without arguments, it fetches all positions at once.
 * When called with pagination parameters, it uses pagination.
 *
 * @param options - Options object containing page and pageSize
 * @returns {object} Object containing positions, pagination metadata, and loading states
 */
export function usePositions(options?: { page?: number; pageSize?: number }) {
  const account = useAccount();
  const chainId = useChainId();
  const queryClient = useQueryClient();

  // Check if the function was called without arguments
  const fetchAll = !options;

  // Default values
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? 20;

  // If options weren't provided, use a large pageSize to get all positions in one query
  const effectivePageSize = fetchAll ? Number.MAX_SAFE_INTEGER : pageSize;

  // When fetching all, always use page 0
  const effectivePage = fetchAll ? 0 : page;

  // Create the combined query options
  const queryOptions = useMemo(
    () => ({
      // Unique query key based on chain, account, page, and pageSize
      queryKey: QUERY_KEY_POSITIONS_WITH_DETAILS(
        chainId,
        (account.address as `0x${string}`) || ZERO_ADDR,
        effectivePage,
        effectivePageSize,
      ),
      // Query function that fetches both IDs and details
      queryFn: async () =>
        fetchPositionsWithDetails({
          queryClient,
          chainId,
          address: account.address as `0x${string}`,
          page: effectivePage,
          pageSize: effectivePageSize,
        }),
      // Only enable the query if an account is connected
      enabled: !!account.address,
      // Use constants from querySettings for consistent stale time
      staleTime: DEFAULT_POSITIONS_STALE_TIME,
      // Add more specific options to improve the UX
      refetchOnWindowFocus: true, // Refetch when user comes back to the tab
      // Keep previous data to prevent jumps during pagination
      placeholderData: keepPreviousData,
    }),
    [chainId, account.address, effectivePage, effectivePageSize, queryClient],
  );

  // Use TanStack Query's suspense mode for loading states
  const { data, isPending, isFetching, isLoading } = useSuspenseQuery(queryOptions);

  // Only prefetch adjacent pages when using pagination (not when fetchAll is true)
  useEffect(() => {
    // Skip prefetching if account not connected, still loading, or fetching all positions
    if (!account.address || isPending || isFetching || fetchAll) return;

    /**
     * Prefetches a specific page of positions
     * @param pageToFetch - The page index to prefetch
     */
    const prefetchPage = (pageToFetch: number) => {
      void queryClient.prefetchQuery({
        queryKey: QUERY_KEY_POSITIONS_WITH_DETAILS(
          chainId,
          account.address as `0x${string}`,
          pageToFetch,
          pageSize,
        ),
        queryFn: async () =>
          fetchPositionsWithDetails({
            queryClient,
            chainId,
            address: account.address as `0x${string}`,
            page: pageToFetch,
            pageSize,
          }),
        staleTime: DEFAULT_POSITIONS_STALE_TIME,
      });
    };

    // Prefetch next page if there are more pages
    if (data?.pagination.hasMore) {
      prefetchPage(page + 1);
    }

    // Prefetch previous page if not on first page
    if (page > 0) {
      prefetchPage(page - 1);
    }
  }, [
    queryClient,
    chainId,
    account.address,
    page,
    pageSize,
    data?.pagination.hasMore,
    isPending,
    isFetching,
    fetchAll,
  ]);

  // Check if data is from the current query or placeholder
  const isPlaceholderData = page !== data?.pagination.currentPage;

  // Return positions, pagination, and loading states
  return {
    positions: data?.positions || [],
    pagination: data?.pagination || {
      totalItems: 0,
      totalPages: 0,
      currentPage: effectivePage,
      hasMore: false,
    },
    isPending,
    isPlaceholderData,
    isFetching,
    isLoading,
  };
}
